/**
 * Firebase Storage service for uploading audio files
 */
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

/**
 * Upload WAV audio file to Firebase Storage
 * @param audioBlob - The WAV audio blob to upload
 * @param userId - User ID for folder structure
 * @param callId - Call ID for file naming
 * @returns Promise with download URL
 */
export const uploadAudioToFirebase = async (
  audioBlob: Blob,
  userId: string,
  callId: string
): Promise<string> => {
  try {
    console.log('📤 Starter upload til Firebase Storage...');
    console.log(`👤 User ID: ${userId}`);
    console.log(`📞 Call ID: ${callId}`);
    console.log(`📁 File size: ${audioBlob.size} bytes`);
    console.log(`📁 File type: ${audioBlob.type}`);
    
    // Opret storage reference med korrekt sti
    const storagePath = `soundfiles/${userId}/${callId}.wav`;
    const storageRef = ref(storage, storagePath);
    
    console.log('📁 Storage path:', storagePath);
    console.log('📁 Full storage ref:', storageRef.toString());
    
    // Upload filen med metadata
    const metadata = {
      contentType: 'audio/wav',
      customMetadata: {
        userId: userId,
        callId: callId,
        uploaded: new Date().toISOString(),
        type: 'training-audio'
      }
    };
    
    console.log('📤 Uploading with metadata:', metadata);
    
    // Upload filen
    const snapshot = await uploadBytes(storageRef, audioBlob, metadata);
    console.log('✅ Upload completed:', snapshot.metadata.name);
    console.log('📊 Upload metadata:', snapshot.metadata);
    
    // Hent download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('🔗 Download URL:', downloadURL);
    
    return downloadURL;
  } catch (error) {
    console.error('❌ Fejl ved upload til Firebase Storage:', error);
    
    // Type-safe error handling
    if (error instanceof Error) {
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
    } else {
      console.error('❌ Unknown error type:', typeof error, error);
    }
    
    throw error;
  }
};

/**
 * Generate a unique call ID
 * @returns Unique call ID string
 */
export const generateCallId = (): string => {
  return `training-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Test Firebase Storage connection
 * @returns Promise<boolean> - true if connection works
 */
export const testFirebaseStorageConnection = async (): Promise<boolean> => {
  try {
    console.log('🧪 Testing Firebase Storage connection...');
    console.log('📦 Storage instance:', storage);
    console.log('📦 Storage app:', storage.app);
    
    // Test med en lille test fil
    const testBlob = new Blob(['test'], { type: 'text/plain' });
    const testRef = ref(storage, 'test/connection-test.txt');
    
    console.log('📁 Test ref:', testRef);
    console.log('📁 Test ref bucket:', testRef.bucket);
    console.log('📁 Test ref fullPath:', testRef.fullPath);
    
    console.log('📤 Uploading test file...');
    const snapshot = await uploadBytes(testRef, testBlob);
    console.log('✅ Test upload successful:', snapshot.metadata.name);
    console.log('📊 Snapshot metadata:', snapshot.metadata);
    
    // Cleanup test file
    console.log('🧹 Cleaning up test file...');
    // Note: We can't delete from client-side, but that's OK for testing
    
    return true;
  } catch (error) {
    console.error('❌ Firebase Storage connection test failed:', error);
    if (error instanceof Error) {
      console.error('❌ Test error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
    } else {
      console.error('❌ Unknown error type:', typeof error, error);
    }
    return false;
  }
};

/**
 * Simple Firebase Storage connection test (without upload)
 * @returns Promise<boolean> - true if connection works
 */
export const testFirebaseStorageConnectionSimple = async (): Promise<boolean> => {
  try {
    console.log('🧪 Testing Firebase Storage connection (simple)...');
    
    // Just test if we can create a reference
    const testRef = ref(storage, 'test/connection-test.txt');
    console.log('✅ Storage reference created successfully');
    console.log('📁 Test ref bucket:', testRef.bucket);
    console.log('📁 Test ref fullPath:', testRef.fullPath);
    
    return true;
  } catch (error) {
    console.error('❌ Simple Firebase Storage test failed:', error);
    if (error instanceof Error) {
      console.error('❌ Simple test error:', error.message);
    }
    return false;
  }
};
